// Opt-in performance profiler. Zero overhead unless turned on with
//   ?perf=1   in the URL, or   localStorage.pcp_perf = "1"
//
// When on, it wraps the hot paths every controller funnels through —
// JSON.parse / JSON.stringify and localStorage get/set — plus any code that
// calls perfTime(). That captures the aggregate cost of plan handling across
// ALL ~25 controllers without editing each one.
//
// In the console:
//   pcpPerf()         → table of {calls, totalMs, avgMs, maxMs} per label
//   pcpPerfReset()    → zero the counters (call before an isolated action)
//   pcpPerfMark("x")  → record a one-off timestamp since page load
//
// Typical use: load the editor with ?perf=1, run pcpPerf() right after load
// (that's the BOOT cost), then pcpPerfReset(), type ~20 chars, pcpPerf()
// again (that's the TYPING cost), reset, switch a section, pcpPerf().

const ENABLED = (() => {
  try {
    if (new URLSearchParams(window.location.search).has("perf")) {
      try { localStorage.setItem("pcp_perf", "1") } catch (_) {}
      return true
    }
    return localStorage.getItem("pcp_perf") === "1"
  } catch (_) { return false }
})()

const stats = Object.create(null)
function bump(label, ms) {
  const s = stats[label] || (stats[label] = { calls: 0, total: 0, max: 0 })
  s.calls++
  s.total += ms
  if (ms > s.max) s.max = ms
}

// Time a synchronous function under `label`. No-op wrapper when disabled.
export function perfTime(label, fn) {
  if (!ENABLED) return fn()
  const t = performance.now()
  try { return fn() }
  finally { bump(label, performance.now() - t) }
}

export function perfEnabled() { return ENABLED }

if (ENABLED && typeof window !== "undefined") {
  const bootAt = performance.now()

  const wrap = (obj, name, label, argIsPayload) => {
    const orig = obj[name]
    obj[name] = function (...args) {
      const t = performance.now()
      try { return orig.apply(this, args) }
      finally {
        let ms = performance.now() - t
        bump(label, ms)
        // Track payload size for the plan blob so we can see how big it is.
        if (argIsPayload) {
          const p = args[argIsPayload - 1]
          if (typeof p === "string" && p.length > 50000) bump(`${label} (>50KB, len=${Math.round(p.length / 1024)}KB)`, ms)
        }
      }
    }
  }

  wrap(JSON, "parse", "JSON.parse", 1)
  wrap(JSON, "stringify", "JSON.stringify", 0)
  wrap(Storage.prototype, "getItem", "localStorage.getItem", 0)
  wrap(Storage.prototype, "setItem", "localStorage.setItem", 2)

  window.pcpPerf = () => {
    const rows = {}
    for (const k of Object.keys(stats)) {
      const v = stats[k]
      rows[k] = {
        calls: v.calls,
        totalMs: Math.round(v.total * 10) / 10,
        avgMs: Math.round((v.total / v.calls) * 100) / 100,
        maxMs: Math.round(v.max * 100) / 100
      }
    }
    console.table(rows)
    console.log(`(since page load: ${Math.round(performance.now() - bootAt)} ms)`)
    return rows
  }
  window.pcpPerfReset = () => { for (const k of Object.keys(stats)) delete stats[k] }
  window.pcpPerfMark = (label) => {
    const ms = Math.round(performance.now() - bootAt)
    console.log(`[perf] ${label}: ${ms} ms since load`)
    return ms
  }

  console.log("%c[perf] profiling ON — run pcpPerf() in the console. Turn off: localStorage.removeItem('pcp_perf')", "color:#6366f1;font-weight:bold")
}
